import React, { useEffect, useRef, useState } from "react";
import api, { imageUrl } from "../lib/api";
import ImageField from "./ImageField";
import { uploadImage } from "./uploadImage";
import { Plus, Pencil, Trash2, X, Loader2, Upload, ArrowLeft } from "lucide-react";

const CATEGORIES = ["kitchen", "bathroom", "basement", "custom", "outdoor", "commercial", "full-home"];

const empty = {
  title: "", category: "kitchen", location: "Edmonton", budget: "", length: "",
  description: "", tag: "", cover: null, images: [], before_after: [], featured: false, order: 0,
};

export default function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // project object or null
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    setProjects((p) => p.filter((x) => x.id !== id));
  };

  if (editing) {
    return <ProjectEditor project={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-white font-display text-4xl font-bold mb-1">Projects <span className="text-[#6b7683] text-2xl">({projects.length})</span></h1>
          <p className="text-[#6b7683]">Manage your portfolio. Up to 10 photos per project plus before/after.</p>
        </div>
        <button onClick={() => setEditing(empty)} className="flex items-center gap-2 px-5 py-3 rounded-lg bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors">
          <Plus className="w-5 h-5" /> New Project
        </button>
      </div>

      {loading ? (
        <div className="text-[#6b7683]">Loading...</div>
      ) : projects.length === 0 ? (
        <div className="text-[#6b7683] bg-[#141a22] rounded-xl p-12 text-center border border-white/5">No projects yet. Click “New Project” to add your first one.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((p) => (
            <div key={p.id} className="bg-[#141a22] rounded-xl overflow-hidden border border-white/5 group">
              <div className="h-44 bg-[#0b0e13]">
                {p.cover ? <img src={imageUrl(p.cover)} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#3a434e]">No cover</div>}
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded bg-[#2f9fe6]/15 text-[#2f9fe6] text-xs capitalize">{p.category}</span>
                  <span className="text-[#6b7683] text-xs">{(p.images || []).length} photos</span>
                </div>
                <h3 className="text-white font-semibold mb-1">{p.title}</h3>
                <p className="text-[#6b7683] text-sm mb-4">{p.location}</p>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(p)} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10"><Pencil className="w-4 h-4" /> Edit</button>
                  <button onClick={() => remove(p.id)} className="px-3 py-2 rounded-lg bg-white/5 text-[#9aa4af] hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectEditor({ project, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...project });
  const [saving, setSaving] = useState(false);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const galleryInput = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 10 - form.images.length;
    const toUpload = files.slice(0, room);
    setGalleryBusy(true);
    try {
      for (const file of toUpload) {
        const id = await uploadImage(file);
        setForm((f) => ({ ...f, images: [...f.images, id], cover: f.cover || id }));
      }
    } finally {
      setGalleryBusy(false);
      if (galleryInput.current) galleryInput.current.value = "";
    }
  };
  const removeGallery = (id) => set("images", form.images.filter((x) => x !== id));

  const addBA = () => set("before_after", [...form.before_after, { before: null, after: null, label: "" }]);
  const setBA = (i, k, v) => set("before_after", form.before_after.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
  const removeBA = (i) => set("before_after", form.before_after.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title.trim()) { alert("Please enter a title"); return; }
    setSaving(true);
    try {
      const payload = { ...form, tag: form.tag || form.category };
      if (project.id) await api.put(`/admin/projects/${project.id}`, payload);
      else await api.post("/admin/projects", payload);
      onSaved();
    } catch (err) {
      alert("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#0b0e13] border border-white/10 text-white focus:border-[#2f9fe6] outline-none transition-colors";

  return (
    <div className="max-w-4xl">
      <button onClick={onClose} className="flex items-center gap-2 text-[#9aa4af] hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Back to projects</button>
      <h1 className="text-white font-display text-3xl font-bold mb-8">{project.id ? "Edit" : "New"} Project</h1>

      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-5">
          <div><div className="text-sm text-[#9aa4af] mb-1.5">Title</div><input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Modern Elegance Kitchen" /></div>
          <div><div className="text-sm text-[#9aa4af] mb-1.5">Category</div>
            <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#141a22] capitalize">{c}</option>)}
            </select>
          </div>
          <div><div className="text-sm text-[#9aa4af] mb-1.5">Location</div><input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Windermere" /></div>
          <div><div className="text-sm text-[#9aa4af] mb-1.5">Display tag (optional)</div><input className={inputCls} value={form.tag} onChange={(e) => set("tag", e.target.value)} placeholder="Kitchen" /></div>
          <div><div className="text-sm text-[#9aa4af] mb-1.5">Budget</div><input className={inputCls} value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="$60,000–$70,000" /></div>
          <div><div className="text-sm text-[#9aa4af] mb-1.5">Timeline</div><input className={inputCls} value={form.length} onChange={(e) => set("length", e.target.value)} placeholder="10 Weeks" /></div>
        </div>

        <div><div className="text-sm text-[#9aa4af] mb-1.5">Description</div><textarea rows={4} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the project..." /></div>

        <div>
          <div className="text-sm text-[#9aa4af] mb-1.5">Cover image</div>
          <ImageField value={form.cover} onChange={(id) => set("cover", id)} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-[#9aa4af]">Gallery photos ({form.images.length}/10)</div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {form.images.map((id) => (
              <div key={id} className="relative h-24 rounded-lg overflow-hidden group border border-white/10">
                <img src={imageUrl(id)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeGallery(id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                {form.cover === id && <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-[#2f9fe6] text-white text-[10px]">Cover</span>}
                {form.cover !== id && <button type="button" onClick={() => set("cover", id)} className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] opacity-0 group-hover:opacity-100">Set cover</button>}
              </div>
            ))}
            {form.images.length < 10 && (
              <button type="button" onClick={() => galleryInput.current?.click()} disabled={galleryBusy} className="h-24 rounded-lg border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1 text-[#6b7683] hover:border-[#2f9fe6] hover:text-[#2f9fe6] transition-colors">
                {galleryBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                <span className="text-[10px]">Add</span>
              </button>
            )}
          </div>
          <input ref={galleryInput} type="file" accept="image/*" multiple onChange={addGallery} className="hidden" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-[#9aa4af]">Before / After</div>
            <button type="button" onClick={addBA} className="flex items-center gap-1.5 text-[#2f9fe6] text-sm font-medium"><Plus className="w-4 h-4" /> Add pair</button>
          </div>
          <div className="space-y-4">
            {form.before_after.map((b, i) => (
              <div key={i} className="bg-[#141a22] rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <input className="bg-transparent text-white outline-none text-sm" value={b.label} onChange={(e) => setBA(i, "label", e.target.value)} placeholder="Label (e.g. Kitchen transformation)" />
                  <button type="button" onClick={() => removeBA(i)} className="text-[#6b7683] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <ImageField small value={b.before} onChange={(id) => setBA(i, "before", id)} label="Before" />
                  <ImageField small value={b.after} onChange={(id) => setBA(i, "after", id)} label="After" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 pt-4">
          <button onClick={save} disabled={saving} className="px-8 py-3 rounded-lg bg-[#2f9fe6] text-white font-semibold hover:bg-[#1f82c7] transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save Project"}</button>
          <button onClick={onClose} className="px-6 py-3 rounded-lg text-[#9aa4af] hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}
